import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { Users, FileText, Sparkles, Mail, Library, Loader2, Download } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500"><Icon className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">{label}</span></div>
    <p className="text-3xl font-bold text-slate-900 mt-2">{value}</p>
  </div>
);

const AdminOverview = ({ onAuthError }) => {
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    adminApi.get("/admin/stats").then((r) => setStats(r.data)).catch(onAuthError);
  }, []);

  const exportCsv = async (kind) => {
    setDownloading(kind);
    try {
      const res = await adminApi.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `tomorrowsjob_${kind}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${kind}.csv`);
    } catch (e) { onAuthError(e); toast.error("Export failed"); }
    finally { setDownloading(""); }
  };

  if (!stats) return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const maxSign = Math.max(1, ...stats.signups_7d.map((d) => d.count));
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="flex flex-wrap gap-2 justify-end">
        <Button variant="outline" onClick={() => exportCsv("users")} disabled={downloading === "users"} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="export-users-csv">
          {downloading === "users" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Users CSV
        </Button>
        <Button variant="outline" onClick={() => exportCsv("content")} disabled={downloading === "content"} className="border-slate-300 text-slate-700 hover:bg-slate-100" data-testid="export-content-csv">
          {downloading === "content" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />} Content CSV
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard icon={Users} label="Users" value={stats.users} testId="stat-users" />
        <StatCard icon={FileText} label="Resumes" value={stats.resumes} testId="stat-resumes" />
        <StatCard icon={Sparkles} label="Generated" value={stats.generated} testId="stat-generated" />
        <StatCard icon={Mail} label="Cover Letters" value={stats.cover_letters} testId="stat-covers" />
        <StatCard icon={Library} label="Roles" value={stats.roles} testId="stat-roles" />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
          <h3 className="font-bold text-slate-900 mb-4">Signups · last 7 days</h3>
          <div className="flex items-end gap-2 h-32">
            {stats.signups_7d.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t bg-sky-50" style={{ height: `${(d.count / maxSign) * 100}%`, minHeight: d.count ? 4 : 0 }} title={`${d.count}`} />
                <span className="text-[9px] text-slate-500">{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] p-5">
          <h3 className="font-bold text-slate-900 mb-4">Top generated roles</h3>
          {stats.top_roles.length === 0 ? <p className="text-sm text-slate-500">No resumes generated yet.</p> : (
            <ul className="space-y-2">
              {stats.top_roles.map((t) => (
                <li key={t.title} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{t.title}</span>
                  <span className="font-mono text-sky-600">{t.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
